import type { MouseEvent, PropsWithChildren } from "react"
import { forwardRef, useCallback, useEffect, useRef, useState } from "react"

import Masonry from "@mui/lab/Masonry"
import { Box, Stack, Typography, useMediaQuery } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"

import type { FC } from ".."
import { BlogArticleTeaser, Container, Link } from ".."
import { LinkType } from "../../graphql/types"
import { BlogArticleTeaserProps } from "../BlogArticleTeaser/BlogArticleTeaser"

type BlogCategorySectionProps = {
  title: string
  link: LinkType
  items: BlogArticleTeaserProps[]
  variant: string
  backgroundColor: string
  onLike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  onUnlike?: (arg: { contentType?: string; nid?: number }, arg2?: string) => void
  componentType?: string
}

const BlogCategorySection: FC<BlogCategorySectionProps> = ({
  title,
  link,
  items,
  variant = "fourItems",
  backgroundColor = "white",
  onLike,
  onUnlike,
  componentType,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      "(min-width: 992px)": {
        active: false,
      },
    },
  })

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const snaps = emblaApi.scrollSnapList()
      setScrollSnaps(snaps)

      if (containerRef.current && scrollTrackRef.current) {
        const { clientWidth, scrollWidth } = containerRef.current
        const { clientWidth: trackSize } = scrollTrackRef.current
        setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20))
      }
    },
    [containerRef, scrollTrackRef]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const handleTrackClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef
      const { current: contentCurrent } = containerRef

      if (!emblaApi || !trackCurrent || !contentCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef, containerRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, containerRef, scrollTrackRef, onInit, onScroll])

  return (
    <Box
      component="section"
      overflow="hidden"
      sx={(theme) => {
        switch (backgroundColor) {
          case "orange":
            return {
              "bgcolor": theme.palette["tertiary.300"].main,

              "& + * > section > div:first-of-type": {
                borderTop: 0,
              },
            }
          default:
            return {
              bgcolor: theme.palette["neutral.100"].main,
            }
        }
      }}
    >
      <Container>
        <Box
          py={{ xs: 20, md: 30 }}
          sx={(theme) => ({
            ...(backgroundColor === "white" && {
              borderTop: `1px solid ${theme.palette["neutral.200"].main}`,
            }),
          })}
        >
          <BlogCategorySectionHeader title={title} link={link} />
          <Box ref={emblaRef} overflow={{ xs: "hidden", xl: "unset" }}>
            <BlogCategorySectionGrid ref={containerRef} variant={variant}>
              {items?.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    ...(variant === "threeCards" && {
                      "&:first-child": { img: { height: { xl: "456px" } } },
                    }),
                    ...(variant === "fourCards" && {
                      p: 5,
                    }),
                  }}
                >
                  <BlogArticleTeaser
                    {...item}
                    noBorder={variant === "threeCards"}
                    layout={variant === "threeCards" ? "vertical" : "horizontal"}
                    onLike={onLike}
                    onUnlike={onUnlike}
                    componentType={componentType}
                  />
                </Box>
              ))}
            </BlogCategorySectionGrid>
            <Box
              width="100%"
              height="2px"
              mt={{ xs: 10, md: 15 }}
              position="relative"
              sx={{
                display: { lg: "none" },
              }}
            >
              <Box
                ref={scrollTrackRef}
                onClick={(e) => handleTrackClick(e)}
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bgcolor={(theme) => theme.palette["neutral.200"].main}
                sx={{ "&:hover": { cursor: "pointer" } }}
              />
              <Box
                ref={scrollThumbRef}
                position="absolute"
                width={thumbWidth}
                top={0}
                left={0}
                bottom={0}
                bgcolor={(theme) => theme.palette["primary.800"].main}
              />
            </Box>
          </Box>
          <Box px={2.5} pt={12} display={{ lg: "none" }}>
            <Link
              href={link.uri}
              type="base"
              variant="link.sm"
              sx={{
                borderRadius: 1,
                border: (theme) => `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 50,
                px: 7,
                py: 3.5,
              }}
            >
              {link.title}
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default BlogCategorySection

/** A sub-component for the blog category section header  */
const BlogCategorySectionHeader: FC<Pick<BlogCategorySectionProps, "title" | "link">> = ({
  title,
  link,
}) => {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ md: "center" }}
      mb={{ xs: 10, md: 15 }}
      gap={{ xs: 5, md: 0 }}
    >
      <Box>
        <Typography variant="heading.xl" component="h2">
          {title}
        </Typography>
      </Box>
      <Box display={{ xs: "none", lg: "flex" }}>
        <Link
          type="base"
          variant="link.sm"
          href={link.uri}
          sx={(theme) => ({
            borderRadius: 1,
            border: (theme) => `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 50,
            px: 7,
            py: 3.5,
          })}
        >
          {link.title}
        </Link>
      </Box>
    </Stack>
  )
}

/** A sub-component for the blog category section grid  */
type GridProps = PropsWithChildren<Pick<BlogCategorySectionProps, "variant">>
const BlogCategorySectionGrid = forwardRef<HTMLDivElement, GridProps>(
  ({ variant, children }, ref) => {
    const theme = useTheme()
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"))

    if (variant === "threeCards")
      return (
        <Box
          display="grid"
          gridAutoFlow={{ xs: "column", lg: "row" }}
          gridAutoColumns="minmax(calc(100% - 50px), auto)"
          gridTemplateColumns={{
            lg: "repeat(3, minmax(0, 1fr))",
            xl: "minmax(0, 1.33fr) minmax(0, 1fr) minmax(0, 1fr)",
          }}
          gap={{ xs: 4, xl: 10 }}
          pb={2}
        >
          {children}
        </Box>
      )

    if (isLarge)
      return (
        <Box mx={-5}>
          <Masonry ref={ref} columns={2}>
            <>{children}</>
          </Masonry>
        </Box>
      )

    return (
      <Box
        ref={ref}
        display="grid"
        gridAutoFlow={{ xs: "column", lg: "row" }}
        gridAutoColumns="minmax(308px, auto)"
        gridTemplateColumns={{ lg: "1fr", xl: "repeat(2, 1fr)" }}
        gap={{ xs: 4, xl: 10 }}
        pb={2}
      >
        {children}
      </Box>
    )
  }
)
BlogCategorySectionGrid.displayName = "BlogCategorySectionGrid"
